import CatalogArticleGroup from '@/components/contracts/CatalogArticleGroup'
import CatalogSaveBar from '@/components/contracts/CatalogSaveBar'
import { ArticleService, Contract, ContractArticleService, SharedProps } from '@/types'
import { Head, Link, router } from '@inertiajs/react'
import { Alert, Box, Button, Group, Paper, Stack, Switch, Text, TextInput, Title } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconArrowLeft, IconInfoCircle, IconSearch } from '@tabler/icons-react'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'

interface ContractCatalogPageProps extends SharedProps {
  contract: Contract
  article_services: ArticleService[]
  contract_article_services: ContractArticleService[]
}

interface OverrideState {
  enabled: boolean
  price: number | null
}

export default function ContractCatalogPage({
  contract,
  article_services,
  contract_article_services,
  current_organization,
}: ContractCatalogPageProps) {
  const initialOverrides = useMemo(
    () => Object.fromEntries(
      contract_article_services.map((entry) => [
        entry.article_service_id,
        { enabled: true, price: entry.price === null ? null : Number(entry.price) },
      ])
    ),
    [contract_article_services],
  )

  const [curatedCatalog, setCuratedCatalog] = useState(contract.curated_catalog)
  const [overrides, setOverrides] = useState<Record<number, OverrideState>>(initialOverrides)
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 200)
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set())

  const groupedByArticle = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase()
    const groups: Record<string, ArticleService[]> = {}
    article_services.forEach((item) => {
      const matchesSearch = !needle || item.article_name.toLowerCase().includes(needle) || item.service_name.toLowerCase().includes(needle)
      if (!matchesSearch) return
      if (!groups[item.article_name]) groups[item.article_name] = []
      groups[item.article_name].push(item)
    })
    return groups
  }, [article_services, debouncedSearch])

  const setEnabled = useCallback((articleServiceId: number, value: boolean) => {
    setOverrides((current) => {
      const next = { ...current }
      const existing = next[articleServiceId]
      if (value) {
        next[articleServiceId] = { enabled: true, price: existing?.price ?? null }
      } else if (existing && existing.price !== null) {
        next[articleServiceId] = { enabled: false, price: existing.price }
      } else {
        delete next[articleServiceId]
      }
      return next
    })
  }, [])

  const setPrice = useCallback(
    (articleServiceId: number, value: number | null) => {
      setOverrides((current) => {
        const next = { ...current }
        const existing = next[articleServiceId]
        if (value === null && (!existing || !existing.enabled)) {
          delete next[articleServiceId]
        } else {
          next[articleServiceId] = { enabled: existing?.enabled ?? !curatedCatalog, price: value }
        }
        return next
      })
    },
    [curatedCatalog],
  )

  const toggleArticleGroup = useCallback((services: ArticleService[], value: boolean) => {
    setOverrides((current) => {
      const next = { ...current }
      services.forEach((service) => {
        const existing = next[service.id]
        if (value) {
          next[service.id] = { enabled: true, price: existing?.price ?? null }
        } else if (existing && existing.price !== null) {
          next[service.id] = { enabled: false, price: existing.price }
        } else {
          delete next[service.id]
        }
      })
      return next
    })
  }, [])

  const toggleGroupOpen = useCallback((articleName: string) => {
    setOpenGroups((current) => {
      const next = new Set(current)
      if (next.has(articleName)) {
        next.delete(articleName)
      } else {
        next.add(articleName)
      }
      return next
    })
  }, [])

  useEffect(() => {
    if (!debouncedSearch.trim()) return
    setOpenGroups((current) => {
      const visible = Object.keys(groupedByArticle)
      if (visible.every((name) => current.has(name))) return current
      return new Set([...current, ...visible])
    })
  }, [debouncedSearch, groupedByArticle])

  const currentSerialized = useMemo(() => {
    const map = new Map<number, string>()
    Object.entries(overrides).forEach(([id, value]) => {
      const include = curatedCatalog ? value.enabled : value.price !== null
      if (!include) return
      map.set(Number(id), value.price === null ? '' : String(value.price))
    })
    return map
  }, [overrides, curatedCatalog])

  const changeCount = useMemo(() => {
    const baseline = new Map(
      contract_article_services.map((e) => [e.article_service_id, e.price === null ? '' : String(Number(e.price))])
    )
    let count = curatedCatalog === contract.curated_catalog ? 0 : 1
    const ids = new Set([...baseline.keys(), ...currentSerialized.keys()])
    ids.forEach((id) => { if (baseline.get(id) !== currentSerialized.get(id)) count++ })
    return count
  }, [currentSerialized, curatedCatalog, contract_article_services, contract.curated_catalog])

  const handleSubmit = () => {
    const items = Array.from(currentSerialized.entries()).map(([article_service_id, price]) => ({
      article_service_id,
      price,
    }))

    router.patch(
      `/organizations/${current_organization!.slug}/contracts/${contract.id}/catalog`,
      {
        catalog: {
          curated_catalog: curatedCatalog,
          items,
        },
      },
      { preserveScroll: true },
    )
  }

  const handleDiscard = () => {
    setCuratedCatalog(contract.curated_catalog)
    setOverrides(initialOverrides)
  }

  const handleCuratedCatalogChange = (event: ChangeEvent<HTMLInputElement>) =>
    setCuratedCatalog(event.currentTarget.checked)

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => setSearch(event.currentTarget.value)

  return (
    <>
      <Head title={`Catalogue de ${contract.customer_name} - ${current_organization?.name}`} />

      <Stack gap="xl">
        <Group>
          <Button
            component={Link}
            href={`/organizations/${current_organization?.slug}/contracts/${contract.id}`}
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            color="gray"
          >
            Retour au contrat
          </Button>
        </Group>

        <Stack gap="xs">
          <Title order={2}>Catalogue de {contract.customer_name}</Title>
          <Text c="dimmed">
            Activez les articles disponibles pour ce contrat et personnalisez leurs prix si besoin.
          </Text>
        </Stack>

        <Paper withBorder radius="md" p="md">
          <Switch
            checked={curatedCatalog}
            onChange={handleCuratedCatalogChange}
            label="Restreindre les articles disponibles"
            description="Lorsque activé, seuls les articles cochés ci-dessous seront proposés pour ce contrat."
          />
        </Paper>

        {!curatedCatalog && (
          <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
            Le contrat utilise le catalogue complet. Les prix renseignés ci-dessous remplaceront le prix par défaut du contrat.
          </Alert>
        )}

        <TextInput
          placeholder="Rechercher un article ou un service..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={handleSearchChange}
        />

        <Stack gap="sm">
          {Object.entries(groupedByArticle).map(([articleName, services]) => (
            <CatalogArticleGroup
              key={articleName}
              articleName={articleName}
              services={services}
              curatedCatalog={curatedCatalog}
              overrides={overrides}
              isOpen={openGroups.has(articleName)}
              onToggleOpen={toggleGroupOpen}
              onSetEnabled={setEnabled}
              onSetPrice={setPrice}
              onToggleAll={toggleArticleGroup}
            />
          ))}
        </Stack>

        {Object.keys(groupedByArticle).length === 0 && (
          <Box ta="center" py="xl">
            <Text c="dimmed">Aucun article ne correspond à la recherche.</Text>
          </Box>
        )}
      </Stack>

      <CatalogSaveBar changeCount={changeCount} onDiscard={handleDiscard} onSubmit={handleSubmit} />
    </>
  )
}
